import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query'
import { useHasPermission } from '@/lib/hooks/usePermission'
import { useToast } from '@/lib/hooks/useToast'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import type { Tenant } from '@/lib/services/tenantService'
import { CheckCircle2, XCircle, Calendar, Globe, Hash, AlertCircle } from 'lucide-react'
import { AdminConsentUrlDialog } from './AdminConsentUrlDialog'
import { tenantService } from '@/lib/services/tenantService'
import { getRoles } from '@/lib/services/rbacService'

interface ViewTenantDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  tenant: Tenant
}

/**
 * Dialog for viewing tenant details (read-only)
 */
export function ViewTenantDialog({ open, onOpenChange, tenant }: ViewTenantDialogProps) {
  const { t } = useTranslation()
  const [isAdminConsentDialogOpen, setIsAdminConsentDialogOpen] = useState(false)

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleString()
  }

  const queryClient = useQueryClient()
  const { toast } = useToast()
  const canManageTenants = useHasPermission('tenants.manage')

  // Fetch available Crisis Buddy roles for display
  const { data: rolesData } = useQuery({
    queryKey: ['roles'],
    queryFn: () => getRoles(),
  })

  const availableRoles = rolesData?.roles || []

  const activateMutation = useMutation({
    mutationFn: (tenantId: string) => tenantService.activate(tenantId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] })
      toast({
        title: t('admin.tenants.activateSuccess', 'Tenant Activated'),
        description: t(
          'admin.tenants.activateSuccessDescription',
          'Users from this tenant can now sign in.'
        ),
        variant: 'success',
      })
    },
    onError: (error: unknown) => {
      const errorMessage = error instanceof Error ? error.message : String(error)
      toast({
        title: t('admin.tenants.activateError', 'Activation Failed'),
        description: errorMessage,
        variant: 'destructive',
      })
    },
  })

  const deactivateMutation = useMutation({
    mutationFn: (tenantId: string) => tenantService.deactivate(tenantId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] })
      toast({
        title: t('admin.tenants.deactivateSuccess', 'Tenant Deactivated'),
        description: t(
          'admin.tenants.deactivateSuccessDescription',
          'Users from this tenant can no longer sign in.'
        ),
        variant: 'success',
      })
    },
    onError: (error: unknown) => {
      const errorMessage = error instanceof Error ? error.message : String(error)
      toast({
        title: t('admin.tenants.deactivateError', 'Deactivation Failed'),
        description: errorMessage,
        variant: 'destructive',
      })
    },
  })

  const handleToggleStatus = async () => {
    if (!canManageTenants) return
    if (tenant.isActive) {
      await deactivateMutation.mutateAsync(tenant.id)
    } else {
      await activateMutation.mutateAsync(tenant.id)
    }
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('admin.tenants.tenantDetails', 'Tenant Details')}</DialogTitle>
            <DialogDescription>
              {t('admin.tenants.viewTenantDescription', 'View detailed information about this tenant.')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Status */}
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">{t('admin.tenants.status', 'Status')}</span>
              <div className="flex items-center gap-2">
                {tenant.isActive ? (
                  <Badge variant="default" className="flex items-center gap-1">
                    <CheckCircle2 className="h-3 w-3" />
                    {t('admin.tenants.active', 'Active')}
                  </Badge>
                ) : (
                  <Badge variant="destructive" className="flex items-center gap-1">
                    <XCircle className="h-3 w-3" />
                    {t('admin.tenants.inactive', 'Inactive')}
                  </Badge>
                )}

                {canManageTenants && (
                  <Button
                    size="sm"
                    onClick={handleToggleStatus}
                    disabled={activateMutation.isPending || deactivateMutation.isPending}
                  >
                    {tenant.isActive
                      ? t('admin.tenants.deactivate', 'Deactivate')
                      : t('admin.tenants.activate', 'Activate')}
                  </Button>
                )}
              </div>
            </div>

            {/* Basic Information */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  {t('admin.tenants.basicInformation', 'Basic Information')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-3 gap-2">
                  <span className="text-sm text-muted-foreground flex items-center gap-2">
                    <Hash className="h-4 w-4" />
                    {t('admin.tenants.tenantId', 'Tenant ID')}
                  </span>
                  <span className="col-span-2 text-sm font-mono">{tenant.tenantId}</span>
                </div>

                <div className="grid grid-cols-3 gap-2">
                  <span className="text-sm text-muted-foreground flex items-center gap-2">
                    <Globe className="h-4 w-4" />
                    {t('admin.tenants.tenantName', 'Tenant Name')}
                  </span>
                  <span className="col-span-2 text-sm font-medium">{tenant.tenantName}</span>
                </div>

                <div className="grid grid-cols-3 gap-2">
                  <span className="text-sm text-muted-foreground flex items-center gap-2">
                    <Globe className="h-4 w-4" />
                    {t('admin.tenants.domain', 'Domain')}
                  </span>
                  <span className="col-span-2 text-sm">{tenant.domain}</span>
                </div>
              </CardContent>
            </Card>

            {/* Admin Consent */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  {t('admin.tenants.adminConsent', 'Admin Consent')}
                </CardTitle>
                <CardDescription>
                  {t(
                    'admin.tenants.adminConsentInfo',
                    'Admin consent is required for the application to access tenant resources.'
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-3 gap-2">
                  <span className="text-sm text-muted-foreground">
                    {t('admin.tenants.consentStatus', 'Status')}
                  </span>
                  <div className="col-span-2">
                    {tenant.adminConsentGranted ? (
                      <Badge variant="default">{t('admin.tenants.granted', 'Granted')}</Badge>
                    ) : (
                      <Badge variant="secondary">{t('admin.tenants.pending', 'Pending')}</Badge>
                    )}
                  </div>
                </div>

                {tenant.adminConsentDate && (
                  <div className="grid grid-cols-3 gap-2">
                    <span className="text-sm text-muted-foreground flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      {t('admin.tenants.consentDate', 'Granted On')}
                    </span>
                    <span className="col-span-2 text-sm">{formatDate(tenant.adminConsentDate)}</span>
                  </div>
                )}

                {!tenant.adminConsentGranted && (
                  <Button
                    onClick={() => setIsAdminConsentDialogOpen(true)}
                    className="w-full"
                  >
                    {t('admin.tenants.generateConsentUrl', 'Generate Consent URL')}
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* Role Mapping */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  {t('admin.tenants.roleMapping', 'Role Mapping')}
                </CardTitle>
                <CardDescription>
                  {t(
                    'admin.tenants.fixedRoleMappingInfo',
                    'Role mapping uses a fixed convention. All Entra app roles must follow the pattern "crisisbuddy.<slug>"'
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    {t(
                      'admin.tenants.fixedMappingExplanation',
                      'Entra app roles are automatically mapped based on a fixed convention: crisisbuddy.<slug> → <slug>. For example, "crisisbuddy.admin" becomes "admin".'
                    )}
                  </AlertDescription>
                </Alert>

                <div className="bg-muted p-4 rounded space-y-3">
                  <p className="text-sm font-medium">
                    {t('admin.tenants.requiredAppRoles', 'Required Entra App Roles')}:
                  </p>
                  <div className="space-y-2">
                    {availableRoles?.map(role => (
                      <div key={role.id} className="flex items-center justify-between text-sm">
                        <code className="bg-background px-2 py-1 rounded font-mono text-xs">
                          crisisbuddy.{role.slug}
                        </code>
                        <span className="text-muted-foreground">→</span>
                        <Badge variant="secondary">{role.name}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Metadata */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  {t('admin.tenants.metadata', 'Metadata')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-3 gap-2">
                  <span className="text-sm text-muted-foreground flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    {t('admin.tenants.addedAt', 'Added At')}
                  </span>
                  <span className="col-span-2 text-sm">
                    {tenant.addedAt ? new Date(tenant.addedAt).toLocaleDateString() : '-'}
                  </span>
                </div>

                <div className="grid grid-cols-3 gap-2">
                  <span className="text-sm text-muted-foreground flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    {t('admin.tenants.addedOn', 'Added On')}
                  </span>
                  <span className="col-span-2 text-sm">{formatDate(tenant.addedAt)}</span>
                </div>

                <div className="grid grid-cols-3 gap-2">
                  <span className="text-sm text-muted-foreground flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    {t('admin.tenants.lastLogin', 'Last Login')}
                  </span>
                  <span className="col-span-2 text-sm">{formatDate(tenant.lastLoginAt)}</span>
                </div>

                <div className="grid grid-cols-3 gap-2">
                  <span className="text-sm text-muted-foreground flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    {t('admin.tenants.updatedAt', 'Last Updated')}
                  </span>
                  <span className="col-span-2 text-sm">{formatDate(tenant.updatedAt)}</span>
                </div>
              </CardContent>
            </Card>

            {/* Notes */}
            {tenant.notes && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">{t('admin.tenants.notes', 'Notes')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap">{tenant.notes}</p>
                </CardContent>
              </Card>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <AdminConsentUrlDialog
        open={isAdminConsentDialogOpen}
        onOpenChange={setIsAdminConsentDialogOpen}
        tenant={tenant}
      />
    </>
  )
}
